import { Link } from "react-router-dom";
import { Table } from "@legal-500/legal500-design-system";
import type { Column } from "@legal-500/legal500-design-system";
import { useQuery } from "@tanstack/react-query";
import { researchCms } from "@/api/researchCms.service";
import type { ImportHistoryEntry } from "@/api/researchCms.service";
import { ROUTES } from "../../../../../routes";
import { ALL_KINDS, importHistoryLink } from "../../utils/bpaImport";
import { firmNameEntries } from "../../utils/firmNames";
import { describeCounts, formatImportDate } from "./importText";
import shared from "./importShared.module.css";
import styles from "./ImportHistoryList.module.css";

type HistoryRow = {
  id: string;
  direction: string;
  practiceArea: React.ReactNode;
  content: React.ReactNode;
  date: string;
  ranBy: string;
};

const COLUMNS: Column<HistoryRow>[] = [
  { header: "Direction", accessor: "direction" },
  { header: "Practice area", accessor: "practiceArea", render: value => value },
  { header: "Content", accessor: "content", wrap: true, render: value => value },
  { header: "Date", accessor: "date" },
  { header: "Run by", accessor: "ranBy" },
];

type ImportHistoryListProps = {
  editionId: string;
  editionBpaId: string | null;
  entries: ImportHistoryEntry[];
  firmNamesUnavailable?: boolean;
};

const FIRM_NAMES_SHOWN = 3;
const FIRM_NAMES_UNAVAILABLE_TEXT = "Firm names couldn't be loaded.";

function movedNothing(entry: ImportHistoryEntry): boolean {
  const isSplitOrMerge = entry.purpose === "split" || entry.purpose === "merge";
  return (
    isSplitOrMerge &&
    entry.importedSubmissions + entry.importedRankings + entry.importedCommentary === 0
  );
}

function contentOf(entry: ImportHistoryEntry): string {
  if (movedNothing(entry)) return "Nothing moved";
  const kinds = ALL_KINDS.filter(kind => entry.contentTypes.includes(kind));
  return describeCounts(
    {
      submissions: entry.importedSubmissions,
      rankings: entry.importedRankings,
      commentary: entry.importedCommentary,
    },
    kinds,
  );
}

function firmNamesText(
  companies: Array<{ name: string | null }>,
  firmNamesUnavailable: boolean,
): string | null {
  const names = companies.map(company => company.name);
  const known = names.filter((name): name is string => !!name).slice(0, FIRM_NAMES_SHOWN);
  const { shown, more } = firmNamesUnavailable
    ? { shown: known, more: companies.length - known.length }
    : firmNameEntries(names, FIRM_NAMES_SHOWN);
  if (shown.length === 0) return null;
  if (more > 0) return `${shown.join(", ")} and ${more} more`;
  return shown.length > 1
    ? `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`
    : shown[0];
}

function removedLine(
  deletions: NonNullable<ImportHistoryEntry["targetDeletions"]>,
  from: string,
  firmNamesUnavailable: boolean,
): string | null {
  const kinds = ALL_KINDS.filter(kind => deletions[kind] > 0);
  if (kinds.length === 0) return null;
  const counts = describeCounts(deletions, kinds);
  const firmCount = deletions.companies.length;
  const firms = firmCount > 0 ? ` for ${firmCount} ${firmCount === 1 ? "firm" : "firms"}` : "";
  const names = firmNamesText(deletions.companies, firmNamesUnavailable);
  return `Removed ${counts} from ${from}${firms}${names ? `: ${names}` : ""}.`;
}

function removedFromTargetLines(
  entry: ImportHistoryEntry,
  from: string,
  firmNamesUnavailable: boolean,
): string[] {
  if (!entry.targetDeletions) return [];
  const line = removedLine(entry.targetDeletions, from, firmNamesUnavailable);
  if (!line) return [];
  const hasFirms = entry.targetDeletions.companies.length > 0;
  return firmNamesUnavailable && hasFirms ? [line, FIRM_NAMES_UNAVAILABLE_TEXT] : [line];
}

/** Every import into and out of one BPA, newest first, with the other BPA, the counts, the date and who ran it. */
export function ImportHistoryList({
  editionId,
  editionBpaId,
  entries,
  firmNamesUnavailable = false,
}: ImportHistoryListProps) {
  const actorIds = [
    ...new Set(entries.map(entry => entry.performedBy).filter((id): id is string => !!id)),
  ].sort();
  const userNames = useQuery({
    queryKey: ["research-user-names", actorIds],
    queryFn: () => researchCms.getUserNames(actorIds),
    enabled: actorIds.length > 0,
  });
  const nameById = userNames.data?.names ?? {};

  if (entries.length === 0) {
    return <p className={styles.empty}>No imports into or out of this practice area yet.</p>;
  }

  const rows: HistoryRow[] = entries.map(entry => {
    const link = importHistoryLink(entry, editionBpaId ?? "", editionId);
    return {
      id: entry.id,
      direction: link.direction,
      practiceArea: (
        <Link className={styles.link} to={ROUTES.research.cmsBpa(link.editionId, link.bpaId)}>
          {link.label}
        </Link>
      ),
      content: (
        <>
          <span>{contentOf(entry)}</span>
          {removedFromTargetLines(
            entry,
            entry.targetEditionBpaId === editionBpaId ? "this practice area" : link.label,
            firmNamesUnavailable,
          ).map(line => (
            <span key={line} className={styles.removed}>
              {line}
            </span>
          ))}
        </>
      ),
      date: formatImportDate(entry.createdAt),
      ranBy: (entry.performedBy && nameById[entry.performedBy]) || "Unknown user",
    };
  });

  return (
    <div className={shared.box}>
      <Table data={rows} columns={COLUMNS} ariaLabel="Import history" />
    </div>
  );
}
